import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Pencil, DollarSign, Eye, MousePointerClick, Target, Percent, Gauge } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { getAds, createAd, updateAd, removeAd } from "../../api";

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4 text-brand" /> {label}
      </div>
      <p className="mt-1 text-2xl font-black">{value}</p>
    </div>
  );
}

const blank = { name: "", spend: "", impressions: "", clicks: "", conversions: "" };

export default function AdsTracking() {
  const [ads, setAds] = useState([]);
  const [totals, setTotals] = useState({ spend: 0, impressions: 0, clicks: 0, conversions: 0, ctr: 0, cpa: 0 });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(blank);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getAds().then((d) => { setAds(d.campaigns); setTotals(d.totals); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setDraft(blank); setOpen(true); };
  const openEdit = (ad) => { setDraft(ad); setOpen(true); };

  const save = async () => {
    if (!draft.name) return toast.error("請輸入廣告名稱");
    const payload = {
      name: draft.name,
      spend: Number(draft.spend || 0),
      impressions: Number(draft.impressions || 0),
      clicks: Number(draft.clicks || 0),
      conversions: Number(draft.conversions || 0),
    };
    try {
      if (draft.id) await updateAd(draft.id, payload);
      else await createAd(payload);
      setOpen(false);
      toast.success("廣告已儲存");
      load();
    } catch { toast.error("儲存失敗"); }
  };

  const remove = async (id) => {
    await removeAd(id);
    toast.success("已刪除廣告");
    load();
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Metric icon={DollarSign} label="總花費" value={`$${Number(totals.spend).toLocaleString()}`} />
        <Metric icon={Eye} label="曝光" value={Number(totals.impressions).toLocaleString()} />
        <Metric icon={MousePointerClick} label="點擊" value={Number(totals.clicks).toLocaleString()} />
        <Metric icon={Target} label="轉換" value={Number(totals.conversions).toLocaleString()} />
        <Metric icon={Percent} label="CTR" value={`${totals.ctr}%`} />
        <Metric icon={Gauge} label="CPA" value={`$${Number(totals.cpa).toLocaleString()}`} />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">廣告列表</h3>
        <Button onClick={openNew} className="bg-brand font-bold text-white hover:bg-[#EA580C]">
          <Plus className="mr-1 h-4 w-4" /> 新增廣告
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-white shadow-sm">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-secondary text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-3">廣告名稱</th><th className="px-3 py-3">花費</th>
              <th className="px-3 py-3">曝光</th><th className="px-3 py-3">點擊</th>
              <th className="px-3 py-3">轉換</th><th className="px-3 py-3">CTR</th>
              <th className="px-3 py-3">CPA</th><th className="px-3 py-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">載入中…</td></tr>}
            {!loading && ads.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">尚無廣告，點右上角新增</td></tr>}
            {!loading && ads.map((a) => {
              const ctr = a.impressions ? ((a.clicks / a.impressions) * 100).toFixed(2) : "0";
              const cpa = a.conversions ? Math.round(a.spend / a.conversions) : 0;
              return (
                <tr key={a.id} className="hover:bg-orange-50/40">
                  <td className="px-3 py-3 font-medium">{a.name}</td>
                  <td className="px-3 py-3">${Number(a.spend).toLocaleString()}</td>
                  <td className="px-3 py-3">{Number(a.impressions).toLocaleString()}</td>
                  <td className="px-3 py-3">{Number(a.clicks).toLocaleString()}</td>
                  <td className="px-3 py-3">{Number(a.conversions).toLocaleString()}</td>
                  <td className="px-3 py-3">{ctr}%</td>
                  <td className="px-3 py-3">${cpa.toLocaleString()}</td>
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(a)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(a.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{draft.id ? "編輯廣告" : "新增廣告"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name", "廣告名稱", "text"], ["spend", "花費 ($)", "number"], ["impressions", "曝光", "number"], ["clicks", "點擊", "number"], ["conversions", "轉換", "number"]].map(([k, label, type]) => (
              <div key={k}>
                <Label className="mb-1 block text-sm">{label}</Label>
                <Input type={type} value={draft[k]} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>取消</Button>
            <Button onClick={save} className="bg-brand text-white hover:bg-[#EA580C]">儲存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
